import { isBanccardTokenizationKeyFormat, normalizeBanccardTokenizationKey } from '@serviceflow/shared'

export const DEFAULT_BANCCARD_COLLECT_JS_URL =
  'https://banccardvirtual.transactiongateway.com/token/Collect.js'

export type CollectJsPayment = 'creditcard' | 'check'

const SCRIPT_ID = 'sf-banccard-collect-js'

const COLLECT_FIELD_IDS = [
  'banccard-ccnumber',
  'banccard-ccexp',
  'banccard-cvv',
  'banccard-checkname',
  'banccard-checkaba',
  'banccard-checkaccount',
]

declare global {
  interface Window {
    CollectJS?: {
      configure: (options: Record<string, unknown>) => void
      startPaymentRequest?: () => void
    }
  }
}

export function resetCollectJs(): void {
  delete window.CollectJS
  document.getElementById(SCRIPT_ID)?.remove()
  document.querySelectorAll('script[src*="token/Collect.js"]').forEach((node) => node.remove())
  for (const id of COLLECT_FIELD_IDS) {
    document.getElementById(id)?.replaceChildren()
  }
}

function withResetGlobalRegexLastIndex<T>(fn: () => T): T {
  const originalMatch = String.prototype.match
  String.prototype.match = function match(this: string, regexp: string | RegExp) {
    if (regexp instanceof RegExp && regexp.global) {
      regexp.lastIndex = 0
    }
    return originalMatch.call(this, regexp)
  }
  try {
    return fn()
  } finally {
    String.prototype.match = originalMatch
  }
}

export function loadCollectJs(scriptUrl: string, tokenizationKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.CollectJS) {
      resolve()
      return
    }
    const script = document.createElement('script')
    const key = normalizeBanccardTokenizationKey(tokenizationKey)
    if (!isBanccardTokenizationKeyFormat(key)) {
      reject(
        new Error(
          'Invalid BancCard tokenization key. It must look like xxxxxx-xxxxxx-xxxxxx-xxxxxx. Use a Collect.js Tokenization key from Settings → Security Keys, not the API security key.'
        )
      )
      return
    }
    script.id = SCRIPT_ID
    script.src = scriptUrl
    script.async = false
    // Collect.js treats every data-* attribute as config. Only the tokenization key is valid.
    script.setAttribute('data-tokenization-key', key)
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Collect.js'))
    document.body.appendChild(script)
  })
}

export function configureCollectJs(input: {
  payment: CollectJsPayment
  tokenizationKey: string
  onToken: (token: string) => void
  onError?: (message: string) => void
}): void {
  if (!window.CollectJS) {
    input.onError?.('Collect.js is not loaded')
    return
  }

  const cardReady = Boolean(document.querySelector('#banccard-ccnumber'))
  const checkReady = Boolean(document.querySelector('#banccard-checkname'))
  const useCheck = input.payment === 'check' && checkReady
  if (!useCheck && !cardReady) {
    input.onError?.('BancCard card fields are not ready')
    return
  }

  const fields = useCheck
    ? {
        checkname: { selector: '#banccard-checkname', placeholder: 'Name on account' },
        checkaba: { selector: '#banccard-checkaba', placeholder: 'Routing number' },
        checkaccount: { selector: '#banccard-checkaccount', placeholder: 'Account number' },
      }
    : {
        ccnumber: { selector: '#banccard-ccnumber', placeholder: 'Card number' },
        ccexp: { selector: '#banccard-ccexp', placeholder: 'MM / YY' },
        cvv: { selector: '#banccard-cvv', placeholder: 'CVV' },
      }

  const tokenizationKey = normalizeBanccardTokenizationKey(input.tokenizationKey)
  const fieldCss = {
    'background-color': 'transparent',
    'border': 'none',
    'color': '#111827',
    'font-family': '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    'font-size': '16px',
    'height': '44px',
    'line-height': '44px',
    'padding': '0 12px',
    'width': '100%',
  }
  const placeholderCss = {
    color: '#9ca3af',
    'font-size': '16px',
  }
  withResetGlobalRegexLastIndex(() => {
    window.CollectJS!.configure({
    tokenizationKey,
    paymentSelector: '#banccard-pay-button',
    variant: 'inline',
    styleSniffer: true,
    customCss: fieldCss,
    placeholderCss,
    focusCss: {
      outline: 'none',
      color: '#111827',
    },
    invalidCss: {
      color: '#b91c1c',
    },
    fields,
    callback: (response: { token?: string; paymentToken?: string }) => {
      const token = response.token || response.paymentToken
      if (!token) {
        input.onError?.('Collect.js did not return a payment token')
        return
      }
      input.onToken(token)
    },
    validationCallback: (_field: string, valid: boolean, message: string) => {
      if (!valid && message) {
        input.onError?.(message)
      }
    },
    timeoutDuration: 10000,
    timeoutCallback: () => input.onError?.('Collect.js timed out. Try again.'),
    fieldsAvailableCallback: () => undefined,
    })
  })
}
